import {
  dolomiteIsolationMarket,
  type DolomiteIsolationMarket,
} from '@1delta/data-sdk'
import { GenericTokenList } from '../../../types'
import { toOracleKey } from '../../../assets'
import { createMarketUid } from '../../../utils'
import { lenderShortName } from '../../../utils/marketName'

/**
 * Isolation-mode facts a Dolomite market row carries. Everything a caller
 * needs to know that the row's `underlying` is a per-user VAULT FACTORY
 * (`dGM`, `dsavETH`, `dGMX`, …) and not a token anyone holds:
 *
 * - `underlying` is what the user deposits / receives (GM, savETH, GMX);
 *   `factory` is the market token itself.
 * - `allowedDebtMarketUids` / `allowedCollateralMarketUids` are the vault's
 *   allow-lists as market uids. `undefined` = unrestricted (the on-chain
 *   list is empty) — never confuse with "no debt allowed".
 * - `wrapper` / `unwrapper` are the trusted converter traders a zap must use
 *   as the last / first hop; `null` = no loop route through this market.
 * - `async` = GMX V2 / GLV: the wrap is a keeper-executed GMX deposit paid
 *   with `executionFeeWei` of native, and the vault is frozen meanwhile.
 */
export interface DolomiteIsolationRow {
  factory: string
  underlying: string
  underlyingSymbol: string
  underlyingDecimals: number
  allowedDebtMarketUids?: string[]
  allowedCollateralMarketUids?: string[]
  wrapper: string | null
  unwrapper: string | null
  wrapperInputMarketUids: string[]
  unwrapperOutputMarketUids: string[]
  async: boolean
  executionFeeWei: string | null
}

export interface DolomiteRowIdentity {
  /** The row label (`Dolomite dGM [WETH-USDC]`, `Dolomite WETH`). */
  name: string
  /** The token-list meta the row renders with (the dToken's, logo-filled from the underlying). */
  asset: any
  /**
   * The key the intrinsic-yield join reads: the ASSET GROUP (as every other
   * lender), and for an isolation market the UNDERLYING's group — a dGM
   * earns what its GM earns.
   */
  yieldKey: string
  isolation?: DolomiteIsolationRow
}

const marketUids = (chainId: string, lender: string, ids: string[]) =>
  ids.map((id) => createMarketUid(chainId, lender, String(id)))

/**
 * Resolve name / asset meta / yield key for one Dolomite market, and the
 * isolation block when the market is an isolation-mode factory.
 *
 * The isolation table comes from `config/dolomite-isolation.json`
 * (`dolomiteIsolationMarket`); when it is absent for a chain the token-list
 * overlay's `props.receipt.underlying` still names the underlying, so the
 * yield join and the label degrade gracefully instead of to `dGM` ×12.
 */
export function resolveDolomiteRowIdentity(
  chainId: string,
  lender: string,
  marketId: string | number,
  token: string,
  tokenList: GenericTokenList = {},
): DolomiteRowIdentity {
  const lower = token.toLowerCase()
  const meta: any = tokenList[lower]
  const table: DolomiteIsolationMarket | undefined = dolomiteIsolationMarket(
    chainId,
    marketId,
  )
  const receiptUnderlying: string | undefined =
    meta?.props?.receipt?.protocol === 'dolomite-isolation'
      ? meta.props.receipt.underlying?.toLowerCase()
      : undefined
  const underlying = table?.underlying?.toLowerCase() ?? receiptUnderlying

  if (!underlying) {
    return {
      name: lenderShortName(lender) + ' ' + (meta?.symbol ?? ''),
      asset: meta,
      yieldKey: toOracleKey(meta?.assetGroup ?? null),
    }
  }

  const underlyingMeta: any = tokenList[underlying]
  const symbol =
    meta?.symbol ??
    (underlyingMeta?.symbol
      ? 'd' + underlyingMeta.symbol
      : table?.underlyingSymbol
        ? 'd' + table.underlyingSymbol
        : '')
  // The dToken rows arrived without logos; the receipt IS the underlying
  // economically, so its logo is the right one to show.
  const asset =
    meta && !meta.logoURI && underlyingMeta?.logoURI
      ? { ...meta, logoURI: underlyingMeta.logoURI }
      : meta

  const isolation: DolomiteIsolationRow | undefined = table
    ? {
        factory: table.factory,
        underlying,
        underlyingSymbol: table.underlyingSymbol,
        underlyingDecimals: table.underlyingDecimals,
        allowedDebtMarketUids:
          table.allowableDebtMarketIds.length > 0
            ? marketUids(chainId, lender, table.allowableDebtMarketIds)
            : undefined,
        allowedCollateralMarketUids:
          table.allowableCollateralMarketIds.length > 0
            ? marketUids(chainId, lender, table.allowableCollateralMarketIds)
            : undefined,
        wrapper: table.wrapper,
        unwrapper: table.unwrapper,
        wrapperInputMarketUids: marketUids(
          chainId,
          lender,
          table.wrapperInputMarketIds,
        ),
        unwrapperOutputMarketUids: marketUids(
          chainId,
          lender,
          table.unwrapperOutputMarketIds,
        ),
        async: table.async,
        executionFeeWei: table.executionFeeWei,
      }
    : undefined

  return {
    name: lenderShortName(lender) + ' ' + symbol,
    asset,
    // The underlying's group; fall back to the dToken's own group only when
    // the underlying is unlisted (then nothing is keyed on it anyway).
    yieldKey: toOracleKey(
      underlyingMeta?.assetGroup ?? meta?.assetGroup ?? null,
    ),
    isolation,
  }
}
